import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2 } from "lucide-react";
import { MtgLogo } from "@/components/MtgLogo";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  const brand = settings.brand_primary || "#0D9488";
  const logoProps = { name: settings.business_name, logoUrl: settings.logo_url, tagline: settings.tagline };

  return (
    <div className="flex min-h-screen bg-slate-900">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-slate-900 p-12 lg:flex">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `radial-gradient(circle at 20% 20%, ${brand} 0, transparent 40%), radial-gradient(circle at 80% 70%, ${brand} 0, transparent 45%)` }}
        />
        <div className="relative z-10"><MtgLogo withText {...logoProps} /></div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 max-w-md">
          <h2 className="font-heading text-4xl font-extrabold leading-tight text-white" data-testid="welcome-headline">
            {settings.welcome_headline}
          </h2>
          <p className="mt-4 text-slate-300" data-testid="welcome-subtext">
            {settings.welcome_subtext}
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm" style={{ color: brand }}>
            <ShieldCheck className="h-5 w-5" /> Copy-only outreach · no auto clinical decisions
          </div>
        </motion.div>
        <div className="relative z-10 font-mono text-[11px] text-slate-500">Keys server-side · audit logged</div>
      </div>

      <div className="flex w-full items-center justify-center bg-slate-50 p-8 lg:w-1/2">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="mb-8 flex justify-center lg:justify-start"><MtgLogo className="h-12 w-12" logoUrl={settings.logo_url} name={settings.business_name} /></div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Sign in</h1>
          <p className="mt-1 text-sm text-slate-500">Access the {settings.business_name} workspace</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1.5" placeholder="you@clinic.health" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-teal-700 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full bg-teal-600 hover:bg-teal-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-slate-400" data-testid="welcome-footer">{settings.business_name} · {settings.welcome_footer}</p>
        </motion.div>
      </div>
    </div>
  );
}
